/* eslint-disable ember/no-get, ember/no-new-mixins, ember/require-computed-property-dependencies, no-restricted-properties, warp-drive/no-legacy-request-patterns */
import GroupType from '@linc-technologies/linced-common/enums/group-type';
import Jurisdiction from '@linc-technologies/linced-common/enums/jurisdiction';
import Mixin from '@ember/object/mixin';
import NSWStatus from '@linc-technologies/linced-common/enums/nsw-status';
import PersonType from '@linc-technologies/linced-common/enums/person-type';
import SharingLevel from '@linc-technologies/linced-common/enums/media-sharing-level';
import moment from 'moment';
import { A } from '@ember/array';
import { action, computed } from '@ember/object';
import { dependentKeyCompat } from '@ember/object/compat';
import { filterBy, union } from '@ember/object/computed';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import * as arrays from '@linc-technologies/linced-common/utils/arrays';

export default Mixin.create({
  Jurisdiction,
  NSWStatus,

  // Services
  media: service(),
  options: service(),
  paperToaster: service(),
  session: service(),
  store: service(),
  strings: service(),
  students: service(),

  year: moment().year(),
  groupName: '',
  groupLabel: '',
  groupType: 0,

  groupLabelValidation: computed(
    'session.school.jurisdiction',
    'createTime',
    'groupId',
    'groupType',
    function () {
      // pastCutOff represents the date rollclass shortname validation takes effect
      let pastCutOff = moment
        .unix(this.createTime)
        .isAfter(moment('2025-04-29'));
      if (
        this.session.school.jurisdiction !== Jurisdiction.AU_NSW ||
        this.groupType.value !== GroupType.HOMEROOM
      ) {
        // Return empty array to skip validation for non NSW & homeroom type groups
        return [];
      }
      if (!pastCutOff && !isEmpty(this.groupId)) {
        // Return empty array to skip validation when we're not past the cut off date
        return [];
      }
      return [
        {
          message: 'Group label exceeds 13 characters maximum',
          validate: (val) => val.length <= 13,
        },
        {
          message: 'Group label contains leading or trailing spaces',
          validate: (val) =>
            !(
              val.length > 0 &&
              (val[0] === ' ' || val[val.length - 1] === ' ')
            ),
        },
      ];
    },
  ),

  groupTypeLabel: computed('groupType', 'strings.homeroom', function () {
    let label = this.groupType.label;
    switch (this.groupType.value) {
      case GroupType.SHARED:
      case GroupType.AUTO:
        return `${label} Group`;
      case GroupType.HOMEROOM:
        return this.strings.homeroom;
      default:
        return label;
    }
  }),
  groupTeam: null,
  groupGrouping: null,
  groupStatus: null,
  groupStatuses: Object.freeze([
    { value: 1, label: 'Active' },
    { value: 2, label: 'Inactive' },
  ]),
  selectedTeachers: A([]),
  remainingTeachers: computed('selectedTeachers.[]', function () {
    let allTeachers = this.store
      .peekAll('person')
      .filter((person) => person.type === 2);
    return allTeachers.filter((source) => {
      return !this.selectedTeachers.any(function (teacher) {
        if (!teacher) {
          return false;
        }
        return source.id === teacher.id;
      });
    });
  }),
  emptyArray: A([]),
  selectedStudents: A([]),
  allStudents: computed('students.all.[]', function () {
    // Trigger students service to load all students if not already primed.
    // This property will then recalculate once they're loaded.
    this.get('students.all');
    return this.store
      .peekAll('person')
      .filter((person) => person.type === PersonType.LEARNER);
  }),
  remainingStudents: computed(
    'selectedStudents.[]',
    'allStudents.[]',
    function () {
      return this.allStudents.filter((source) => {
        return !this.selectedStudents.any(function (student) {
          if (!student) {
            return false;
          }
          return source.id === student.id;
        });
      });
    },
  ),

  profileMedia: '',
  image: '',
  createTime: 0,
  uploading: false,

  limitOptions: A([5, 10, 15, 20, 25]),
  limit: 10,
  studentsPages: computed('limit', 'selectedStudents.[]', function () {
    let max = Math.max(
      1,
      Math.ceil(this.get('selectedStudents.length') / this.limit),
    );
    let pages = A([]);
    for (var i = 1; i <= max; i++) {
      pages.pushObject(i);
    }
    return pages;
  }),
  studentsPage: 1,
  sortProp: 'firstName',
  sortDir: 'asc',
  paginatedStudents: computed(
    'studentsPage',
    'limit',
    'sortProp',
    'sortDir',
    'selectedStudents.[]',
    function () {
      let ind = (this.studentsPage - 1) * this.limit;
      let students = A(arrays.sortBy(this.selectedStudents, this.sortProp));
      if (this.sortDir === 'desc') {
        students = students.reverseObjects();
      }

      return A(students.toArray().splice(ind, this.limit));
    },
  ),

  groupDialogOpen: false,
  groupDialogTab: 0,
  saveInProgress: false,
  errorMessage: '',

  groupTypes: GroupType.values,
  GroupType,
  groupTypeLocked: false,
  defaultGroupings: computed('session.school.jurisdiction', function () {
    if (this.session.school.jurisdiction === Jurisdiction.AU_NSW) {
      return A([
        {
          value: 'Excursions / Incursions',
          label: 'Excursions / Incursions',
        },
        {
          value: 'Extra-Curricular',
          label: 'Extra-Curricular',
        },
        {
          value: 'House Groups',
          label: 'House Groups',
        },
        {
          value: 'KLAs/Subjects/Courses',
          label: 'KLAs/Subjects/Courses',
        },
        { value: 'Peer Support', label: 'Peer Support' },
        {
          value: 'Special Religious Education',
          label: 'Special Religious Education',
        },
        { value: 'Sport', label: 'Sport' },
        {
          value: 'Other Activities',
          label: 'Other Activities',
        },
      ]);
    }
    return A();
  }),
  // optionAlias is a native decorator. A mixin uses the classic form of
  // dependentKeyCompat, so the prop is autotracked.
  optionGroupings: dependentKeyCompat({
    get() {
      return this.options.value('core:groups:groupings');
    },
  }),
  groupings: union('defaultGroupings', 'optionGroupings'),
  availableGroupings: computed(
    'groupings.[]',
    'optionGroupings.[]',
    function () {
      // If this is an existing group in NSW it should only be able to be
      // switched to other custom hero group types since group's can't be
      // changed to use a DoE group type, only created with one.
      if (
        this.groupId &&
        this.session.school.jurisdiction === Jurisdiction.AU_NSW
      ) {
        return this.optionGroupings;
      }
      return this.groupings;
    },
  ),
  teams: dependentKeyCompat({
    get() {
      return this.options.value('core:groups:teams');
    },
  }),

  cleanGroupings: filterBy('availableGroupings', 'value'),
  cleanTeams: filterBy('teams', 'value'),

  groupToDelete: null,
  deleteDialogOpen: false,
  deleteInProgress: false,
  onDelete() {},

  homerooms: A(),
  homeroom: null,

  originalGroupType: 0,

  disableGroupings: computed(
    'groupId',
    'session.school.jurisdiction',
    'groupGrouping',
    'defaultGroupings.[]',
    function () {
      // If a group is not selected always allow edits
      if (!this.groupId) {
        return false;
      }
      // Non NSW groups always have the ability to edit groupings
      if (this.session.school.jurisdiction !== Jurisdiction.AU_NSW) {
        return false;
      }
      // If we are in NSW and using a custom grouping instead of a DoE SAIS grouping
      // then the user is able to change the grouping
      return this.defaultGroupings.includes(this.groupGrouping);
    },
  ),

  // toggleDialog displays the group dialog.
  toggleDialog() {
    this.set('errorMessage', '');
    this.toggleProperty('groupDialogOpen');
    if (!this.groupDialogOpen) {
      this.resetForm();
    } else {
      // Populate homeroom list for student selection.
      this.store
        .query('group', {
          groupType: GroupType.HOMEROOM,
          year: moment().year(),
        })
        .then((homerooms) => {
          this.set('homerooms', homerooms);
        });

      if (isEmpty(this.groupId)) {
        // Opened for group creation (not editing)
        // Default group type to selected tab
        let groupType = this.groupTypes[this.selectedTab];
        this.set('groupType', groupType);
      }
    }
  },

  toggleGroupDialog: action(function () {
    this.toggleDialog();
  }),

  editGroup: action(function (id) {
    this.set('selectedTeachers', A([]));
    this.set('selectedStudents', A([]));
    // Wait for all students promise to resolve (if not already) before loading.
    this.get('students.all').then(() => {
      // Await the updated record (with reload: true) to ensure that current
      // learners are properly listed.  Otherwise, if students.all hasn't
      // loaded before we just called it, we get no currentLearners.
      this.store.findRecord('group', id, { reload: true }).then((group) => {
        let teachers = this.selectedTeachers;
        let students = this.selectedStudents;
        let groupType = arrays.findBy(
          this.groupTypes,
          'value',
          group.get('type'),
        );
        let groupTeam = arrays.findBy(this.teams, 'value', group.get('team'));
        let groupGrouping = this.groupings.find(
          (item) => item.value === group.get('grouping'),
        );
        let groupStatus = NSWStatus.ACTIVE;
        if (group.get('status')) {
          groupStatus = arrays.findBy(
            NSWStatus.values,
            'value',
            group.get('status'),
          );
        }
        this.set('groupName', group.get('name'));
        this.set('groupLabel', group.get('label'));
        this.set('profileMedia', group.get('profileMedia'));
        this.set('image', group.get('image'));
        this.set('groupType', groupType);
        this.set('groupTeam', groupTeam);
        this.set('groupGrouping', groupGrouping);
        this.set('originalGroupType', groupType.value);
        this.set('groupStatus', groupStatus);
        this.set('createTime', group.get('createTime'));
        group.get('currentStaff').forEach((member) => {
          teachers.pushObject(member.get('person'));
        });
        group.get('currentLearners').forEach((member) => {
          if (!isEmpty(member.person)) {
            students.pushObject(member.person);
          }
        });
      });
    });
    this.set('groupId', id);
    this.toggleDialog();
  }),

  addTeacher: action(function (teacher) {
    this.selectedTeachers.pushObject(teacher);
  }),

  removeTeacher: action(function (teacher) {
    this.selectedTeachers.removeObject(teacher);
  }),

  addStudent: action(function (student) {
    this.selectedStudents.pushObject(student);
  }),

  removeStudent: action(function (student) {
    this.selectedStudents.removeObject(student);
  }),

  toggleHomeroom: action(function (group, checked) {
    let learners = arrays.mapBy(group.currentLearners, 'person');
    if (checked) {
      this.selectedStudents.pushObjects(learners);
    } else {
      this.selectedStudents.removeObjects(learners);
    }
  }),

  toggleHomeroomLearner: action(function (learner, checked) {
    if (checked) {
      this.selectedStudents.pushObject(learner);
    } else {
      this.selectedStudents.removeObject(learner);
    }
  }),

  isHomeroomSelected: action(function (group) {
    return arrays.mapBy(group.currentLearners, 'person').every((person) => {
      return this.selectedStudents.includes(person);
    });
  }),

  saveGroup: action(function () {
    this.set('saveInProgress', true);
    this.set('errorMessage', '');
    let promise;
    let action = 'create';

    if (isEmpty(this.groupId)) {
      promise = new Promise((resolve) => {
        let record = this.store.createRecord('group', {
          name: this.groupName,
          label: this.groupLabel,
          profileMedia: this.profileMedia,
          type: this.get('groupType.value'),
          schoolId: this.get('session.school'),
          year: this.year,
          status: this.get('groupStatus.value'),
        });

        // As this is a new group, we can simply add new membership records for all members.
        this.selectedTeachers.forEach(function (teacher) {
          record.get('staff').createFragment({
            personId: teacher.id,
            status: 1,
          });
        });
        this.selectedStudents.forEach(function (student) {
          record.get('learners').createFragment({
            personId: student.id,
            status: 1,
          });
        });

        resolve(record);
      });
    } else {
      action = 'update';
      promise = new Promise((resolve) => {
        const id = this.groupId;
        let group = this.store.peekRecord('group', id);

        group.set('name', this.groupName);
        group.set('label', this.groupLabel);
        group.set('profileMedia', this.profileMedia);
        group.set('type', this.groupType.value);
        group.set('status', this.groupStatus.value);

        let staff = group.get('staff');
        let selectedStaff = this.selectedTeachers;
        // Add any new teachers to this group
        selectedStaff.forEach(function (teacher) {
          let found = arrays.findBy(staff, 'personId', teacher.id);
          if (found) {
            // Existing membership record for this teacher - make sure they're set to CURRENT.
            found.set('status', 1);
          } else {
            // No existing membership record for this teacher - add a new one.
            staff.createFragment({
              personId: teacher.id,
              status: 1,
            });
          }
        });

        // Remove any teachers who are no longer associated with this group.
        arrays.filterBy(staff, 'status', 1).forEach(function (member) {
          let found = arrays.findBy(
            selectedStaff,
            'id',
            member.get('personId'),
          );
          if (!found) {
            // Previous teacher no longer selected; set status to FORMER.
            member.set('status', 2);
          }
        });
        // If changing group from Shared to My group type add current user as member staff
        if (
          this.originalGroupType === GroupType.SHARED &&
          this.groupType.value === GroupType.GROUP
        ) {
          let memberFound = staff.find(
            (item) => item.personId === this.get('session.person.id'),
          );
          if (memberFound) {
            // Set status to CURRENT.
            memberFound.set('status', 1);
          } else {
            staff.createFragment({
              personId: this.get('session.person.id'),
              status: 1,
            });
          }
        }
        let learners = group.get('learners');
        let selectedLearners = this.selectedStudents;

        // Add any new teachers to this group
        selectedLearners.forEach(function (student) {
          let found = arrays.findBy(learners, 'personId', student.id);
          if (found) {
            // Existing membership record for this student - make sure they're set to CURRENT.
            found.set('status', 1);
          } else {
            // No existing membership record for this student - add a new one.
            learners.createFragment({
              personId: student.id,
              status: 1,
            });
          }
        });

        // Remove any teachers who are no longer associated with this group.
        arrays.filterBy(learners, 'status', 1).forEach(function (member) {
          let found = arrays.findBy(
            selectedLearners,
            'id',
            member.get('personId'),
          );
          if (!found) {
            // Previous student no longer selected; set status to FORMER.
            member.set('status', 2);
          }
        });

        resolve(group);
      });
    }

    promise.then((record) => {
      switch (record.type) {
        case GroupType.HOMEROOM:
          record.set('team', this.get('groupTeam.value'));
          break;

        case GroupType.GROUP:
        case GroupType.SHARED:
          record.set('grouping', this.get('groupGrouping.value'));
          break;
      }

      return record
        .save()
        .then(() => {
          if (action === 'create') {
            // Let the host refresh its list, if it keeps one.
            this.groupCreated?.();
          }
          this.set('groupDialogOpen', false);
          this.resetForm();
          return Promise.resolve();
        })
        .catch((err) => {
          this.handleError(err, action);
          record.rollbackAttributes();
        })
        .finally(() => {
          this.set('saveInProgress', false);
        });
    });
  }),

  decrementPage: action(function () {
    let page = this.studentsPage;
    if (page > 1) {
      this.set('studentsPage', page - 1);
    }
  }),

  incrementPage: action(function () {
    let page = this.studentsPage;
    let max = this.studentsPages.reduce(
      (prev, curr) => (curr > prev ? curr : prev),
      0,
    );
    if (page < max) {
      this.set('studentsPage', page + 1);
    }
  }),

  // Profile image uploads
  startUpload: action(function () {
    this.set('uploading', true);
  }),

  handleUpload: action(function (file, upload) {
    this.media.handleImageUpload(
      this,
      file,
      upload,
      SharingLevel.SCHOOL,
      this.get('session.school'),
      this.get('session.person'),
      'profileMedia',
      'image',
    );
  }),

  handleUploadError: action(function () {
    this.paperToaster.show(`Error uploading group image!`, {
      duration: 8000,
      position: 'top right',
      toastClass: 'warning',
    });
  }),

  // Deletion of groups
  deleteGroup: action(function () {
    if (isEmpty(this.groupId)) {
      return;
    }

    let group = this.store.peekRecord('group', this.groupId);
    if (isEmpty(group)) {
      return;
    }

    this.setProperties({
      groupToDelete: group,
      deleteDialogOpen: true,
      deleteInProgress: false,
    });
  }),

  confirmDelete: action(function (proceed) {
    if (proceed) {
      this.set('deleteInProgress', true);
      this.groupToDelete
        .destroyRecord()
        .then(() => {
          this.setProperties({
            groupToDelete: null,
            deleteDialogOpen: false,
            deleteInProgress: false,
            groupDialogOpen: false,
          });
          this.onDelete();
        })
        .catch(() => {
          this.groupToDelete.rollbackAttributes();
          this.paperToaster.show(`Error deleting group.`, {
            duration: 8000,
            position: 'top right',
            toastClass: 'warning',
          });
          this.set('deleteInProgress', false);
        });
    } else {
      this.setProperties({
        groupToDelete: null,
        deleteDialogOpen: false,
        deleteInProgress: false,
      });
    }
  }),

  resetForm() {
    this.setProperties({
      groupId: '',
      groupName: '',
      groupLabel: '',
      groupType: 0,
      selectedTeachers: A(),
      selectedStudents: A(),
      page: 1,
      sortProp: 'firstName',
      sortDir: 'asc',
      groupDialogTab: 0,
      createTime: 0,
      groupTeam: null,
      groupStatus: null,
      groupGrouping: null,
      profileMedia: '',
      image: '',
      groupToDelete: null,
      deleteDialogOpen: false,
      deleteInProgress: false,
      homerooms: A(),
      homeroom: null,
    });
  },
});
